"use client"

import { useEffect, useState, useCallback } from "react"
import { supabase } from "../supabaseClient"
import { Link } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { applyInstitutionFilter } from "../lib/tenant"
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Calendar,
  Building2,
  Layers,
  LogOut,
  UserCheck,
  Archive,
  Building,
  TrendingUp,
  Clock,
  AlertTriangle,
  CheckCircle,
  Settings,
  Activity,
  Zap,
  BarChart3,
  PieChart,
} from "lucide-react"
import "../styles/Dashboard.css"

export default function DashboardPage() {
  const { userProfile, signOut, isAdmin, instituicaoId } = useAuth()
  const [stats, setStats] = useState({
    disciplinas: 0,
    turmas: 0,
    salas: 0,
    usuarios: 0,
    horarios: 0,
    professores: 0,
    cursos: 0,
    departamentos: 0,
  })

  const [estatisticasAvancadas, setEstatisticasAvancadas] = useState({
    horasPorProfessor: [],
    ocupacaoSalas: [],
    disciplinasPorCurso: [],
    horariosPorDia: {},
    turmasSemHorario: 0,
    professoresSemAula: 0,
    totalHorasSemanais: 0,
    mediaAulasPorDia: 0,
  })

  const [userName, setUserName] = useState("")
  const [currentTime, setCurrentTime] = useState(new Date())
  const [showHelpModal, setShowHelpModal] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const fetchStats = useCallback(async () => {
    try {
      const tables = [
        "disciplinas",
        "turmas",
        "salas",
        "usuarios",
        "horarios",
        "professores",
        "cursos",
        "departamentos",
      ]

      const results = await Promise.all(
        tables.map((table) => {
          let q = supabase.from(table).select("*", { count: "exact", head: true })
          q = applyInstitutionFilter(q, isAdmin, instituicaoId)
          return q
        }),
      )

      const [disciplinas, turmas, salas, usuarios, horarios, professores, cursos, departamentos] = results

      setStats({
        disciplinas: disciplinas.count || 0,
        turmas: turmas.count || 0,
        salas: salas.count || 0,
        usuarios: usuarios.count || 0,
        horarios: horarios.count || 0,
        professores: professores.count || 0,
        cursos: cursos.count || 0,
        departamentos: departamentos.count || 0,
      })
    } catch (err) {
      console.error("Erro ao atualizar estatísticas:", err.message)
    }
  }, [isAdmin, instituicaoId])

  const fetchEstatisticasAvancadas = useCallback(async () => {
    try {
      let horariosQuery = supabase.from("horarios").select(`id,
          dia_semana,
          hora_inicio,
          hora_fim,
          professor:professor_id(id, nome),
          sala:sala_id(id, nome, capacidade),
          turma:turma_id(id, nome)
        `)
      horariosQuery = applyInstitutionFilter(horariosQuery, isAdmin, instituicaoId)
      const { data: horariosData } = await horariosQuery

      // Calcular horas por professor
      const horasPorProf = {}
      ;(horariosData || []).forEach((h) => {
        if (h.professor?.nome) {
          horasPorProf[h.professor.nome] = (horasPorProf[h.professor.nome] || 0) + 1
        }
      })

      const horasPorProfessor = Object.entries(horasPorProf)
        .map(([nome, horas]) => ({ nome, horas }))
        .sort((a, b) => b.horas - a.horas)
        .slice(0, 6)

      // Ocupação de salas com capacidade
      const ocupacaoSalas = {}
      const salasInfo = {}
      ;(horariosData || []).forEach((h) => {
        if (h.sala?.nome) {
          ocupacaoSalas[h.sala.nome] = (ocupacaoSalas[h.sala.nome] || 0) + 1
          if (!salasInfo[h.sala.nome]) {
            salasInfo[h.sala.nome] = { capacidade: h.sala.capacidade || 30 }
          }
        }
      })

      const ocupacao = Object.entries(ocupacaoSalas)
        .map(([nome, ocupacao]) => ({
          nome,
          ocupacao,
          capacidade: salasInfo[nome]?.capacidade || 30,
          percentual: Math.min(100, Math.round((ocupacao / 25) * 100)), // 25 = slots por semana
        }))
        .sort((a, b) => b.ocupacao - a.ocupacao)
        .slice(0, 6)

      // Horários por dia da semana
      const horariosPorDia = {}
      ;(horariosData || []).forEach((h) => {
        if (h.dia_semana) {
          horariosPorDia[h.dia_semana] = (horariosPorDia[h.dia_semana] || 0) + 1
        }
      })

      // Total de horas semanais
      const totalHorasSemanais = horariosData?.length || 0

      // Média de aulas por dia
      const diasComAula = Object.keys(horariosPorDia).length || 1
      const mediaAulasPorDia = Math.round(totalHorasSemanais / diasComAula)

      // Turmas sem horário
      let turmasQuery = supabase.from("turmas").select("id")
      turmasQuery = applyInstitutionFilter(turmasQuery, isAdmin, instituicaoId)
      const { data: turmasData } = await turmasQuery
      const turmasComHorario = new Set((horariosData || []).map((h) => h.turma?.id))
      const turmasSemHorario = (turmasData || []).filter((t) => !turmasComHorario.has(t.id)).length

      // Professores sem aula
      let professoresQuery = supabase.from("professores").select("id")
      professoresQuery = applyInstitutionFilter(professoresQuery, isAdmin, instituicaoId)
      const { data: professoresData } = await professoresQuery
      const professoresComAula = new Set((horariosData || []).map((h) => h.professor?.id))
      const professoresSemAula = (professoresData || []).filter((p) => !professoresComAula.has(p.id)).length

      setEstatisticasAvancadas({
        horasPorProfessor,
        ocupacaoSalas: ocupacao,
        horariosPorDia,
        turmasSemHorario,
        professoresSemAula,
        totalHorasSemanais,
        mediaAulasPorDia,
      })
    } catch (err) {
      console.error("Erro ao buscar estatísticas avançadas:", err.message)
    }
  }, [isAdmin, instituicaoId])

  useEffect(() => {
    const init = async () => {
      try {
        // Use userProfile from AuthContext
        if (userProfile?.nome) {
          setUserName(userProfile.nome)
        }

        await fetchStats()
        await fetchEstatisticasAvancadas()

        const tablesToListen = [
          "disciplinas",
          "turmas",
          "salas",
          "usuarios",
          "horarios",
          "professores",
          "cursos",
          "departamentos",
        ]

        const subscriptions = tablesToListen.map((table) =>
          supabase
            .channel(`realtime-${table}`)
            .on("postgres_changes", { event: "*", schema: "public", table }, () => {
              fetchStats()
              fetchEstatisticasAvancadas()
            })
            .subscribe(),
        )

        return () => {
          subscriptions.forEach((sub) => supabase.removeChannel(sub))
        }
      } catch (err) {
        console.error("Erro ao inicializar dashboard:", err.message)
      }
    }

    if (userProfile) {
      init()
    }
  }, [userProfile, fetchStats, fetchEstatisticasAvancadas])

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut()
      signOut()
    } catch (error) {
      alert("Erro ao sair: " + error.message)
      console.error("Erro ao sair:", error.message)
    }
  }

  const menuItems = [
    { name: "Dashboard", path: "/dashboard", icon: <LayoutDashboard size={20} /> },
    { name: "Disciplinas", path: "/disciplinas", icon: <BookOpen size={20} /> },
    { name: "Turmas", path: "/turmas", icon: <Layers size={20} /> },
    { name: "Salas", path: "/salas", icon: <Building2 size={20} /> },
    { name: "Horários", path: "/horarios", icon: <Calendar size={20} /> },
    { name: "Professores", path: "/professores", icon: <UserCheck size={20} /> },
    { name: "Cursos", path: "/cursos", icon: <Archive size={20} /> },
    { name: "Departamentos", path: "/departamentos", icon: <Building size={20} /> },
    { name: "Usuários", path: "/usuarios", icon: <Users size={20} /> },
    { name: "Configurações", path: "/configuracoes", icon: <Settings size={20} /> },
  ]

  const statCards = [
    { title: "Disciplinas", value: stats.disciplinas, icon: <BookOpen size={28} />, gradient: "cyan" },
    { title: "Turmas", value: stats.turmas, icon: <Layers size={28} />, gradient: "green" },
    { title: "Salas", value: stats.salas, icon: <Building2 size={28} />, gradient: "orange" },
    { title: "Professores", value: stats.professores, icon: <UserCheck size={28} />, gradient: "indigo" },
    { title: "Horários", value: stats.horarios, icon: <Calendar size={28} />, gradient: "pink" },
    { title: "Cursos", value: stats.cursos, icon: <Archive size={28} />, gradient: "teal" },
    { title: "Departamentos", value: stats.departamentos, icon: <Building size={28} />, gradient: "purple" },
    { title: "Usuários", value: stats.usuarios, icon: <Users size={28} />, gradient: "slate" },
  ]

  const diasSemana = {
    seg: "Segunda",
    ter: "Terça",
    qua: "Quarta",
    qui: "Quinta",
    sex: "Sexta",
  }

  // Formatar hora
  const formatTime = (date) => {
    return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  }

  const formatDate = (date) => {
    return date.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
  }

  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo-container">
            <div className="logo-icon">
              <Zap size={24} />
            </div>
            <div className="logo-text">
              <span className="logo-title">CajuTime</span>
              <span className="logo-subtitle">Planner</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-menu">
          {menuItems.map((item, index) => (
            <Link
              key={item.name}
              to={item.path}
              className={`sidebar-link ${item.path === "/dashboard" ? "active" : ""}`}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <span className="link-icon">{item.icon}</span>
              <span className="link-text">{item.name}</span>
              <span className="link-indicator"></span>
            </Link>
          ))}
        </nav>

        <button className="logout-btn" onClick={handleLogout}>
          <LogOut size={20} />
          <span>Terminar Sessão</span>
        </button>
      </aside>

      <main className="main-content">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">
              <span className="greeting">Olá</span>
              <span className="user-name">{userName || "Utilizador"}</span>
            </h1>
            <p className="dashboard-subtitle">Bem-vindo ao seu painel de controlo</p>
          </div>
          <div className="header-right">
            <div className="datetime-display">
              <div className="time-display">
                <Clock size={20} />
                <span>{formatTime(currentTime)}</span>
              </div>
              <div className="date-display">{formatDate(currentTime)}</div>
            </div>
          </div>
        </div>

        <div className="quick-metrics">
          <div className="metric-card pulse">
            <div className="metric-icon">
              <Activity size={24} />
            </div>
            <div className="metric-info">
              <span className="metric-value">{estatisticasAvancadas.totalHorasSemanais}</span>
              <span className="metric-label">Aulas/Semana</span>
            </div>
          </div>
          <div className="metric-card pulse" style={{ animationDelay: "0.2s" }}>
            <div className="metric-icon orange">
              <BarChart3 size={24} />
            </div>
            <div className="metric-info">
              <span className="metric-value">{estatisticasAvancadas.mediaAulasPorDia}</span>
              <span className="metric-label">Média/Dia</span>
            </div>
          </div>
          <div className="metric-card pulse" style={{ animationDelay: "0.4s" }}>
            <div className="metric-icon green">
              <CheckCircle size={24} />
            </div>
            <div className="metric-info">
              <span className="metric-value">{stats.turmas - estatisticasAvancadas.turmasSemHorario}</span>
              <span className="metric-label">Turmas Ativas</span>
            </div>
          </div>
          <div className="metric-card pulse" style={{ animationDelay: "0.6s" }}>
            <div className="metric-icon purple">
              <PieChart size={24} />
            </div>
            <div className="metric-info">
              <span className="metric-value">{stats.professores - estatisticasAvancadas.professoresSemAula}</span>
              <span className="metric-label">Prof. Alocados</span>
            </div>
          </div>
        </div>

        <div className="stats-grid">
          {statCards.map((card, index) => (
            <div
              key={card.title}
              className={`stat-card ${card.gradient}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="card-content">
                <div className="card-info">
                  <p className="stat-title">{card.title}</p>
                  <p className="stat-value">{card.value}</p>
                </div>
                <div className="stat-icon">{card.icon}</div>
              </div>
              <div className="card-glow"></div>
              <div className="card-particles">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ))}
        </div>

        <div className="advanced-stats-section">
          <h2 className="section-title">
            <TrendingUp size={24} />
            <span>Análises e Estatísticas</span>
            <div className="title-line"></div>
          </h2>

          <div className="advanced-stats-grid">
            {/* Alertas com design melhorado */}
            <div className="stat-panel alerts-panel glass">
              <h3 className="panel-title">
                <AlertTriangle size={20} />
                <span>Centro de Alertas</span>
              </h3>
              <div className="alerts-list">
                {estatisticasAvancadas.turmasSemHorario > 0 && (
                  <div className="alert-item warning animate-slide">
                    <div className="alert-icon">
                      <AlertTriangle size={18} />
                    </div>
                    <div className="alert-content">
                      <span className="alert-title">Turmas Pendentes</span>
                      <span className="alert-desc">
                        {estatisticasAvancadas.turmasSemHorario} turma(s) sem horário definido
                      </span>
                    </div>
                    <span className="alert-badge">{estatisticasAvancadas.turmasSemHorario}</span>
                  </div>
                )}
                {estatisticasAvancadas.professoresSemAula > 0 && (
                  <div className="alert-item info animate-slide" style={{ animationDelay: "0.1s" }}>
                    <div className="alert-icon">
                      <Clock size={18} />
                    </div>
                    <div className="alert-content">
                      <span className="alert-title">Professores Disponíveis</span>
                      <span className="alert-desc">
                        {estatisticasAvancadas.professoresSemAula} professor(es) sem aulas atribuídas
                      </span>
                    </div>
                    <span className="alert-badge info">{estatisticasAvancadas.professoresSemAula}</span>
                  </div>
                )}
                {estatisticasAvancadas.turmasSemHorario === 0 && estatisticasAvancadas.professoresSemAula === 0 && (
                  <div className="alert-item success animate-slide">
                    <div className="alert-icon">
                      <CheckCircle size={18} />
                    </div>
                    <div className="alert-content">
                      <span className="alert-title">Sistema Otimizado</span>
                      <span className="alert-desc">Tudo em ordem! Sem alertas pendentes.</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Carga Horária por Professor */}
            <div className="stat-panel glass">
              <h3 className="panel-title">
                <UserCheck size={20} />
                <span>Carga Horária por Professor</span>
              </h3>
              <div className="ranking-list">
                {estatisticasAvancadas.horasPorProfessor.length > 0 ? (
                  estatisticasAvancadas.horasPorProfessor.map((prof, idx) => {
                    const maxHoras = Math.max(...estatisticasAvancadas.horasPorProfessor.map((p) => p.horas))
                    const percentage = (prof.horas / maxHoras) * 100
                    return (
                      <div
                        key={prof.nome}
                        className="ranking-item animate-slide"
                        style={{ animationDelay: `${idx * 0.1}s` }}
                      >
                        <span className={`ranking-position pos-${idx + 1}`}>{idx + 1}</span>
                        <div className="ranking-info">
                          <span className="ranking-name">{prof.nome}</span>
                          <div className="ranking-bar">
                            <div className="ranking-bar-fill professor" style={{ width: `${percentage}%` }}>
                              <span className="bar-value">{prof.horas}h</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <p className="no-data">Sem dados disponíveis</p>
                )}
              </div>
            </div>

            {/* Ocupação de Salas */}
            <div className="stat-panel glass">
              <h3 className="panel-title">
                <Building2 size={20} />
                <span>Ocupação das Salas</span>
              </h3>
              <div className="ranking-list">
                {estatisticasAvancadas.ocupacaoSalas.length > 0 ? (
                  estatisticasAvancadas.ocupacaoSalas.map((sala, idx) => (
                    <div
                      key={sala.nome}
                      className="ranking-item animate-slide"
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    >
                      <span className={`ranking-position pos-${idx + 1}`}>{idx + 1}</span>
                      <div className="ranking-info">
                        <span className="ranking-name">{sala.nome}</span>
                        <div className="ranking-bar">
                          <div className="ranking-bar-fill sala" style={{ width: `${sala.percentual}%` }}>
                            <span className="bar-value">{sala.ocupacao} aulas</span>
                          </div>
                        </div>
                        <span className="ranking-percent">{sala.percentual}%</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">Sem dados disponíveis</p>
                )}
              </div>
            </div>

            {/* Distribuição por Dia da Semana */}
            <div className="stat-panel glass">
              <h3 className="panel-title">
                <Calendar size={20} />
                <span>Distribuição Semanal</span>
              </h3>
              <div className="days-chart">
                {Object.entries(diasSemana).map(([key, nome], idx) => {
                  const valor = estatisticasAvancadas.horariosPorDia[key] || 0
                  const maxValor = Math.max(...Object.values(estatisticasAvancadas.horariosPorDia || { x: 1 }), 1)
                  const height = (valor / maxValor) * 100
                  return (
                    <div
                      key={key}
                      className="day-bar-container animate-grow"
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    >
                      <div className="day-bar-wrapper">
                        <div className="day-bar" style={{ height: `${height}%` }}>
                          <span className="day-value">{valor}</span>
                        </div>
                        <div className="day-bar-glow" style={{ height: `${height}%` }}></div>
                      </div>
                      <span className="day-label">{nome.substring(0, 3)}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Card de Ajuda */}
        <div className="help-card-section">
          <div className="help-card glass" onClick={() => setShowHelpModal(true)}>
            <div className="help-card-content">
              <div className="help-icon">
                <BookOpen size={32} />
              </div>
              <div className="help-text">
                <h3>Como Criar Horários</h3>
                <p>Clique aqui para ver o guia passo a passo</p>
              </div>
              <div className="help-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal de Ajuda */}
      {showHelpModal && (
        <div className="help-modal-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>Como Criar Horários - Guia Passo a Passo</h2>
              <button className="help-modal-close" onClick={() => setShowHelpModal(false)}>×</button>
            </div>
            <div className="help-modal-content">
              <div className="help-steps">
                <div className="help-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Cadastre as Disciplinas</h4>
                    <p>Acesse o menu "Disciplinas" e registre todas as matérias que serão ministradas. Defina o tipo (Nuclear ou Global), carga horária semanal e outras informações relevantes.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Cadastre as Salas</h4>
                    <p>No menu "Salas", adicione todas as salas de aula disponíveis. Informe a capacidade e localização para otimizar a distribuição.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Cadastre os Professores</h4>
                    <p>Acesse "Professores" e registre todos os docentes. Vincule cada professor às disciplinas que pode ministrar.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>Cadastre os Cursos</h4>
                    <p>No menu "Cursos", crie os cursos oferecidos pela instituição.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">5</div>
                  <div className="step-content">
                    <h4>Cadastre as Turmas</h4>
                    <p>Acesse "Turmas" e crie as turmas. Vincule cada turma ao curso correspondente, sala padrão e disciplinas obrigatórias.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">6</div>
                  <div className="step-content">
                    <h4>Gere os Horários Automáticos</h4>
                    <p>No menu "Horários", use a opção "Gerar Horários Automáticos" para criar uma distribuição inicial baseada nas regras do sistema.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">7</div>
                  <div className="step-content">
                    <h4>Ajuste Manualmente se Necessário</h4>
                    <p>Use o "Editor de Horários" para fazer ajustes finos, mover aulas ou corrigir conflitos específicos.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">8</div>
                  <div className="step-content">
                    <h4>Verifique e Salve</h4>
                    <p>Revise o horário gerado, verifique conflitos e salve as alterações.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
